/**
 * Lead Detail Modal Component
 * Displays detailed information about a generated lead
 * Part of Lead Generation Redesign
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
  Button,
  Avatar,
  Divider,
  Stack,
  IconButton,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  Collapse,
  Paper,
  Tooltip,
  LinearProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Language as WebIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as AIIcon,
  Star as StarIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Verified as VerifiedIcon,
  AutoAwesome as AutoAwesomeIcon,
  ThumbDown as ThumbDownIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  ThumbDownOutlined as ThumbDownOutlinedIcon,
  PersonAdd as PersonAddIcon,
  WorkOutline as WorkIcon,
  HelpOutline as HelpIcon,
  CheckCircleOutline as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  Visibility as RevealIcon,
  Lock as LockIcon,
  PersonSearch as FindMoreIcon,
} from '@mui/icons-material';
import type { GeneratedLead } from '../types';
import { leadGenerationApi } from '../api/leadGenerationApi';
import { apiClient } from '../api/apiClient';
import ContactDownvoteDialog from './ContactDownvoteDialog';

interface ContactFeedback {
  contactIndex: number;
  vote: 'UP' | 'DOWN';
  rating: number | null;
  reasonChips: string[];
  reasonText: string | null;
}

interface LeadDetailModalProps {
  open: boolean;
  onClose: () => void;
  lead: GeneratedLead | null;
  onAddToCRM: (lead: GeneratedLead, contactData?: { contactName?: string; email?: string; phone?: string }) => Promise<void>;
  onReject: (leadId: string) => Promise<void>;
}

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({
  open,
  onClose,
  lead,
  onAddToCRM,
  onReject,
}) => {
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichmentSteps, setEnrichmentSteps] = useState<Array<{ label: string; status: 'pending' | 'active' | 'done' }>>([]);
  const [addingContactIdx, setAddingContactIdx] = useState<number | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [enrichedLead, setEnrichedLead] = useState<GeneratedLead | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ contactName: '', email: '', phone: '' });
  const [contactRevealed, setContactRevealed] = useState(false);
  const [revealedContactIndices, setRevealedContactIndices] = useState<Set<number>>(new Set());
  const [selectedContactIndices, setSelectedContactIndices] = useState<Set<number>>(new Set());
  const [addedContactIndices, setAddedContactIndices] = useState<Set<number>>(new Set());
  const [contactFeedback, setContactFeedback] = useState<Map<number, ContactFeedback>>(new Map());
  const [downvoteDialogIdx, setDownvoteDialogIdx] = useState<number | null>(null);
  const [isFindingMore, setIsFindingMore] = useState(false);
  const [hasSearchedMore, setHasSearchedMore] = useState(false);
  const [noMoreContactsFound, setNoMoreContactsFound] = useState(false);
  // Open a LinkedIn URL in a centred 1000x800 popup so the user stays in the CRM tab.
  // Falls back to a regular new tab if the popup is blocked.
  const openLinkedInPopup = (url: string) => {
    const w = 1000;
    const h = 800;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const features = `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no`;
    const popup = window.open(url, 'linkedin_profile', features);
    if (!popup) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // --- Contact feedback helpers (thumb up/down) ---
  const submitFeedback = async (
    contactIndex: number,
    payload: { vote: 'UP' | 'DOWN'; rating?: number; reasonChips?: string[]; reasonText?: string },
  ) => {
    if (!lead?.id) return;
    // Optimistic update
    const prev = contactFeedback;
    setContactFeedback(map => {
      const next = new Map(map);
      next.set(contactIndex, {
        contactIndex,
        vote: payload.vote,
        rating: payload.rating ?? null,
        reasonChips: payload.reasonChips ?? [],
        reasonText: payload.reasonText ?? null,
      });
      return next;
    });
    try {
      const resp = await apiClient.post(
        `/lead-generation/generated/${lead.id}/contacts/${contactIndex}/feedback`,
        payload,
      );
      const saved = resp.data?.data;
      if (saved) {
        setContactFeedback(map => {
          const next = new Map(map);
          next.set(contactIndex, saved);
          return next;
        });
      }
    } catch {
      // Rollback on error
      setContactFeedback(prev);
    }
  };

  const removeFeedback = async (contactIndex: number) => {
    if (!lead?.id) return;
    const prev = contactFeedback;
    setContactFeedback(map => {
      const next = new Map(map);
      next.delete(contactIndex);
      return next;
    });
    try {
      await apiClient.delete(`/lead-generation/generated/${lead.id}/contacts/${contactIndex}/feedback`);
    } catch {
      setContactFeedback(prev);
    }
  };

  const handleThumbUp = (contactIndex: number) => {
    const current = contactFeedback.get(contactIndex);
    if (current?.vote === 'UP') {
      void removeFeedback(contactIndex);
    } else {
      void submitFeedback(contactIndex, { vote: 'UP' });
    }
  };

  const handleThumbDown = (contactIndex: number) => {
    // Always open the dialog — either to fill in or to edit existing
    setDownvoteDialogIdx(contactIndex);
  };

  const enrichStepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enrichingLeadIdRef = useRef<string | null>(null);
  const findingMoreLeadIdRef = useRef<string | null>(null);

  // Reset state only when switching to a DIFFERENT lead (not on modal open/close)
  // This allows enrichment to keep running in background when modal is closed
  useEffect(() => {
    // If switching to a different lead, clean up previous state
    if (lead?.id !== enrichingLeadIdRef.current) {
      if (enrichStepTimerRef.current) {
        clearTimeout(enrichStepTimerRef.current);
        enrichStepTimerRef.current = null;
      }
      setIsEnriching(false);
      setEnrichedLead(null);
      setError(null);
      setEnrichmentSteps([]);
      setShowContactForm(false);
      setContactForm({ contactName: '', email: '', phone: '' });
      setContactRevealed(false);
      setRevealedContactIndices(new Set());
      setSelectedContactIndices(new Set());
      setAddedContactIndices(new Set());
      setAddingContactIdx(null);
      // Only reset find-more state if this lead isn't the one being searched
      if (findingMoreLeadIdRef.current !== lead?.id) {
        setIsFindingMore(false);
        setHasSearchedMore(false);
        setNoMoreContactsFound(false);
      }
      enrichingLeadIdRef.current = null;
    }
  }, [lead?.id]);

  // Restore find-more loading state when reopening the same lead
  useEffect(() => {
    if (lead?.id && findingMoreLeadIdRef.current === lead.id) {
      setIsFindingMore(true);
    }
  }, [lead?.id, open]);

  // Load persisted revealed contacts + pending find-more job state when modal opens
  useEffect(() => {
    if (!open || !lead?.id) return;
    const leadId = lead.id;
    leadGenerationApi.getGeneratedLeadById(leadId).then(data => {
      if (data.revealedContactIndices?.length) {
        setRevealedContactIndices(new Set(data.revealedContactIndices));
      }
      const feedback = (data as any).myContactFeedback as ContactFeedback[] | undefined;
      if (Array.isArray(feedback)) {
        setContactFeedback(new Map(feedback.map(f => [f.contactIndex, f])));
      } else {
        setContactFeedback(new Map());
      }
      // If Find-More-Contacts already completed for this lead (in a previous
      // session, or while the modal was closed), the parent-passed `lead`
      // still has the pre-merge contact list. Promote the fresh fetch into
      // enrichedLead so the panel shows the merged contacts.
      const meta = (data as any).metadata as Record<string, any> | undefined;
      if (meta?.moreContactsSearchedAt) {
        setEnrichedLead(data);
        setHasSearchedMore(true);
      }
      // If a Find More Contacts job is queued or running for this lead,
      // resume the searching state and start the poll so the user can't
      // enqueue a duplicate.
      const pendingJobId = (data as any).pendingMoreContactsJobId as string | null | undefined;
      if (pendingJobId && findingMoreLeadIdRef.current !== leadId) {
        findingMoreLeadIdRef.current = leadId;
        setIsFindingMore(true);
        setError(null);

        const maxPolls = 60; // 5 minutes
        let polls = 0;
        const poll = async (): Promise<void> => {
          if (findingMoreLeadIdRef.current !== leadId) return;
          polls++;
          try {
            const updatedLead = await leadGenerationApi.getGeneratedLeadById(leadId);
            const meta = updatedLead?.metadata as any;
            if (meta?.moreContactsSearchedAt) {
              setEnrichedLead(updatedLead);
              setHasSearchedMore(true);
              setSelectedContactIndices(new Set());
              setIsFindingMore(false);
              findingMoreLeadIdRef.current = null;
              return;
            }
            // If the queue row is gone (CANCELLED/FAILED/dismissed), stop polling
            if (!(updatedLead as any).pendingMoreContactsJobId) {
              setIsFindingMore(false);
              findingMoreLeadIdRef.current = null;
              return;
            }
          } catch {/* ignore */}
          if (polls < maxPolls) setTimeout(poll, 5000);
          else {
            setNoMoreContactsFound(true);
            setIsFindingMore(false);
            findingMoreLeadIdRef.current = null;
          }
        };
        setTimeout(poll, 5000);
      }
    }).catch(() => {}); // non-critical — silently ignore
  }, [open, lead?.id]);

  const currentLead = enrichedLead || lead;

  // Check if "find more contacts" was already done (persisted in metadata)
  const alreadySearchedMore = !!(currentLead?.metadata as any)?.moreContactsSearchedAt;

  // Extract scoringFactors from either top-level or metadata
  const getScoringFactors = () => {
    if (currentLead?.scoringFactors) {
      return currentLead.scoringFactors;
    }
    // Check in metadata (backend stores scoringFactors there)
    const metadata = currentLead?.metadata as Record<string, any> | undefined;
    if (metadata?.scoringFactors) {
      return metadata.scoringFactors;
    }
    return undefined;
  };

  const scoringFactors = getScoringFactors();

  if (!lead) return null;

  const getPotentialColor = (rating?: string) => {
    switch (rating) {
      case 'VERY_HIGH': return 'success';
      case 'HIGH': return 'info';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'error';
      default: return 'default';
    }
  };

  const getScoreColor = (score?: number) => {
    if (!score) return 'text.secondary';
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'info.main';
    if (score >= 40) return 'warning.main';
    return 'error.main';
  };

  const getLegitimacyChip = (status: string, score: number) => {
    switch (status) {
      case 'CONVERTED':
        return <Chip icon={<VerifiedIcon />} label={`Proven (${score})`} color="success" size="small" />;
      case 'VERIFIED':
        return <Chip icon={<VerifiedIcon />} label={`Verified (${score})`} color="info" size="small" />;
      case 'REJECTED':
        return <Chip icon={<ThumbDownIcon />} label="Rejected" color="error" size="small" />;
      default:
        return <Chip label="Unverified" size="small" variant="outlined" />;
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value) return null;
    return new Intl.NumberFormat('en-HK', {
      style: 'currency',
      currency: 'HKD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleEnrich = async () => {
    enrichingLeadIdRef.current = lead?.id || null;
    setIsEnriching(true);
    setError(null);

    // Step-by-step enrichment plan (simulated progress while backend works)
    const steps = [
      'Searching company website and public records',
      'Analyzing business profile and industry data',
      'Finding key decision makers and contacts',
      'Verifying contact information',
      'Generating outreach recommendation',
      'Finalizing enrichment report',
    ];

    // Initialize all steps as pending, first as active
    setEnrichmentSteps(steps.map((label, i) => ({
      label,
      status: i === 0 ? 'active' : 'pending',
    })));

    // Progress through steps with realistic timing
    // Steps advance every 15-25 seconds to fill ~2 minutes
    let currentStep = 0;
    const advanceStep = () => {
      currentStep++;
      if (currentStep < steps.length) {
        setEnrichmentSteps(prev => prev.map((step, i) => ({
          ...step,
          status: i < currentStep ? 'done' : i === currentStep ? 'active' : 'pending',
        })));
        // Vary timing: later steps take longer
        const nextDelay = currentStep < 3 ? 12000 + Math.random() * 8000 : 15000 + Math.random() * 15000;
        enrichStepTimerRef.current = setTimeout(advanceStep, nextDelay);
      }
    };
    enrichStepTimerRef.current = setTimeout(advanceStep, 10000 + Math.random() * 5000);

    try {
      // 3-minute timeout — if AI can't find contacts, show a clear message
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('NO_CONTACT_FOUND')), 180000)
      );
      const result = await Promise.race([
        leadGenerationApi.enrichGeneratedLead(lead.id),
        timeoutPromise,
      ]);
      // Mark all steps done on success
      setEnrichmentSteps(prev => prev.map(step => ({ ...step, status: 'done' as const })));
      // Brief pause to show all-done state
      await new Promise(r => setTimeout(r, 600));
      setEnrichedLead(result);
      setEnrichmentSteps([]);
    } catch (err: any) {
      if (err.message === 'NO_CONTACT_FOUND') {
        setError('No valid personal contact is found. The AI could not discover contact details for this company. You may try again later or add contact information manually.');
      } else {
        setError(err.message || 'Failed to enrich lead');
      }
      setEnrichmentSteps([]);
    } finally {
      if (enrichStepTimerRef.current) {
        clearTimeout(enrichStepTimerRef.current);
        enrichStepTimerRef.current = null;
      }
      setIsEnriching(false);
    }
  };

  const handleFindMoreContacts = async () => {
    if (!lead?.id || hasSearchedMore) return;
    const leadId = lead.id;
    findingMoreLeadIdRef.current = leadId;
    setIsFindingMore(true);
    setError(null);
    try {
      // Start async pipeline — returns { jobId, status: 'SEARCHING' } immediately
      await leadGenerationApi.findMoreContacts(leadId);
      window.dispatchEvent(new Event('pipeline-queue-enqueued'));

      // Poll until moreContactsSearchedAt is set (up to 5 minutes)
      const maxPolls = 60; // 60 × 5s = 5 minutes
      let polls = 0;
      const poll = async (): Promise<void> => {
        if (findingMoreLeadIdRef.current !== leadId) return; // lead switched
        polls++;
        try {
          const updatedLead = await leadGenerationApi.getGeneratedLeadById(leadId);
          const meta = updatedLead?.metadata as any;
          if (meta?.moreContactsSearchedAt) {
            setEnrichedLead(updatedLead);
            setHasSearchedMore(true);
            setSelectedContactIndices(new Set());
            setIsFindingMore(false);
            findingMoreLeadIdRef.current = null;
            return;
          }
        } catch {
          // ignore poll errors, keep trying
        }
        if (polls < maxPolls) {
          setTimeout(poll, 5000);
        } else {
          setNoMoreContactsFound(true);
          setIsFindingMore(false);
          findingMoreLeadIdRef.current = null;
        }
      };
      setTimeout(poll, 5000);
    } catch (err: any) {
      if (err?.response?.status === 429 && err?.response?.data?.code === 'PER_USER_CAP_EXCEEDED') {
        setError('You have reached the per-user limit of 10 queued searches. Please wait for some to finish or stop a few from the queue tray.');
        setIsFindingMore(false);
        return;
      }
      const msg = err?.message || '';
      if (/timeout|timed? out|network/i.test(msg)) {
        setNoMoreContactsFound(true);
      } else {
        setError(msg || 'Failed to find more contacts');
      }
      setIsFindingMore(false);
      findingMoreLeadIdRef.current = null;
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    setError(null);
    try {
      await onReject(lead.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to reject lead');
    } finally {
      setIsRejecting(false);
    }
  };

  const displayRecommendation = currentLead?.aiRecommendation;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
            <BusinessIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {currentLead?.companyName}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {currentLead?.industry && (
                <Chip label={currentLead.industry} size="small" variant="outlined" />
              )}
              {currentLead?.potentialRating && (
                <Chip
                  label={currentLead.potentialRating.replace('_', ' ')}
                  color={getPotentialColor(currentLead.potentialRating)}
                  size="small"
                  icon={<StarIcon />}
                />
              )}
              {getLegitimacyChip(currentLead?.legitimacyStatus || 'UNVERIFIED', currentLead?.legitimacyScore || 0)}
            </Stack>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Quick Stats */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            {currentLead?.aiScore !== undefined && (
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">AI Score</Typography>
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" fontWeight="bold" mb={1}>
                            BANT Scoring Framework
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                            • Engagement (0-30): Email opens, clicks, response rate
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                            • Authority (0-30): Decision-making power
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                            • Budget (0-20): Alignment with deal size
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                            • Need (0-10): Interest & industry fit
                          </Typography>
                          <Typography variant="caption" display="block">
                            • Timing (0-10): Contact recency
                          </Typography>
                        </Box>
                      }
                    >
                      <HelpIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" sx={{ color: getScoreColor(currentLead.aiScore), fontWeight: 600 }}>
                    {currentLead.aiScore}%
                  </Typography>
                </Box>
              </Grid>
            )}
            {currentLead?.potentialValue && (
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">Est. Value</Typography>
                  <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                    {formatCurrency(currentLead.potentialValue)}
                  </Typography>
                </Box>
              </Grid>
            )}
            {currentLead?.dataSource && (
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">Data Source</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {currentLead.dataSource}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* AI Score Breakdown - show if scoring factors are available */}
          {scoringFactors && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                AI Score Breakdown (BANT)
              </Typography>
              <Grid container spacing={1}>
                {scoringFactors.engagement !== undefined && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ minWidth: 80 }}>Engagement</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(scoringFactors.engagement / 30) * 100}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { bgcolor: '#1976d2', borderRadius: 3 }
                        }}
                      />
                      <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 35 }}>
                        {scoringFactors.engagement}/30
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {scoringFactors.authority !== undefined && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ minWidth: 80 }}>Authority</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(scoringFactors.authority / 30) * 100}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { bgcolor: '#ed6c02', borderRadius: 3 }
                        }}
                      />
                      <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 35 }}>
                        {scoringFactors.authority}/30
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {scoringFactors.budget !== undefined && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ minWidth: 80 }}>Budget</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(scoringFactors.budget / 20) * 100}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32', borderRadius: 3 }
                        }}
                      />
                      <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 35 }}>
                        {scoringFactors.budget}/20
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {scoringFactors.need !== undefined && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ minWidth: 80 }}>Need</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(scoringFactors.need / 10) * 100}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { bgcolor: '#9c27b0', borderRadius: 3 }
                        }}
                      />
                      <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 35 }}>
                        {scoringFactors.need}/10
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {scoringFactors.timing !== undefined && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ minWidth: 80 }}>Timing</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(scoringFactors.timing / 10) * 100}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { bgcolor: '#0288d1', borderRadius: 3 }
                        }}
                      />
                      <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 35 }}>
                        {scoringFactors.timing}/10
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Paper>

        {/* Company Information */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Company Information (公司資訊)
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {currentLead?.location && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography variant="body2">{currentLead.location}</Typography>
                </Box>
              </Box>
            </Grid>
          )}
          {currentLead?.employeeCount && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Company Size</Typography>
                  <Typography variant="body2">{currentLead.employeeCount} employees</Typography>
                </Box>
              </Box>
            </Grid>
          )}
          {currentLead?.website && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WebIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Website</Typography>
                  <Typography
                    variant="body2"
                    component="a"
                    href={currentLead.website?.startsWith('http') ? currentLead.website : `https://${currentLead.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {currentLead.website.replace(/^https?:\/\//, '')}
                    <OpenInNewIcon fontSize="inherit" />
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          {currentLead?.dataSource && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Data Source</Typography>
                  <Typography variant="body2">{currentLead.dataSource}</Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Description */}
        {currentLead?.description && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Description (描述)
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              {currentLead.description}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Contact Information */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Contact Information (聯絡資訊)
            </Typography>
            {(currentLead?.enrichmentStatus !== 'COMPLETED' || !displayRecommendation) && (
              <Button
                size="small"
                variant="outlined"
                startIcon={isEnriching ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                onClick={handleEnrich}
                disabled={isEnriching}
              >
                {isEnriching ? 'Enriching...' : 'Enrich & Recommend'}
              </Button>
            )}
          </Box>

          {/* Enrichment Step-by-Step Progress */}
          {isEnriching && enrichmentSteps.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderColor: 'primary.200',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="body2" fontWeight={600} color="primary.main">
                  AI Enrichment in Progress
                </Typography>
              </Box>
              <Stack spacing={0.75}>
                {enrichmentSteps.map((step, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      opacity: step.status === 'pending' ? 0.45 : 1,
                      transition: 'opacity 0.3s ease',
                    }}
                  >
                    {step.status === 'done' ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : step.status === 'active' ? (
                      <CircularProgress size={14} thickness={5} sx={{ color: 'primary.main' }} />
                    ) : (
                      <PendingIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: step.status === 'done' ? 'success.main' : step.status === 'active' ? 'text.primary' : 'text.secondary',
                        fontWeight: step.status === 'active' ? 600 : 400,
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                Searching for contacts and business insights...
              </Typography>
            </Paper>
          )}

          {(() => {
            // Build contacts list from metadata.contacts or fallback to single contact fields
            const contactsList: Array<{ contactName?: string; contactEmail?: string; contactPosition?: string; linkedInUrl?: string }> = [];
            const meta = currentLead?.metadata as any;
            // Filter out generic/non-person contacts
            const genericNames = new Set([
              'general enquiries', 'general enquiry', 'general contact', 'sales team',
              'business development', 'hr department', 'info', 'contact', 'support',
              'customer service', 'reception', 'admin', 'office', 'marketing team',
            ]);
            const isRealPerson = (name: string) => {
              if (!name) return false;
              const lower = name.toLowerCase().trim();
              if (genericNames.has(lower)) return false;
              if (lower.split(/\s+/).length < 2) return false;
              if (/\b(team|department|enquir|contact|support|office|general)\b/i.test(lower)) return false;
              return true;
            };

            if (meta?.contacts && Array.isArray(meta.contacts) && meta.contacts.length > 0) {
              contactsList.push(...meta.contacts.filter((c: any) => isRealPerson(c.contactName) && c.contactPosition));
            } else if (currentLead?.contactName && currentLead?.contactPosition && isRealPerson(currentLead.contactName)) {
              contactsList.push({
                contactName: currentLead.contactName,
                contactEmail: currentLead.contactEmail,
                contactPosition: currentLead.contactPosition,
                linkedInUrl: currentLead.linkedInUrl,
              });
            }

            if (contactsList.length === 0) {
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  If you wish to reveal this company's contact personnel information, please press "Enrich & Recommend" below.
                </Alert>
              );
            }

            const handleToggleSelect = (idx: number) => {
              setSelectedContactIndices(prev => {
                const next = new Set(prev);
                if (next.has(idx)) next.delete(idx);
                else next.add(idx);
                return next;
              });
            };

            const handleRevealSelected = () => {
              const newIndices = Array.from(selectedContactIndices);
              setRevealedContactIndices(prev => {
                const next = new Set(prev);
                newIndices.forEach(i => next.add(i));
                return next;
              });
              setSelectedContactIndices(new Set());
              // Persist to backend — records the reveal for credit billing and future modal opens.
              // On success, dispatch a window event so the header credits chip refetches.
              if (currentLead?.id && newIndices.length > 0) {
                leadGenerationApi
                  .revealContacts(currentLead.id, newIndices)
                  .then(() => {
                    window.dispatchEvent(new CustomEvent('credits:reveal'));
                  })
                  .catch(() => {});
              }
            };

            const allRevealed = contactsList.every((_c, i) => revealedContactIndices.has(i));
            const hasSelection = selectedContactIndices.size > 0;

            return (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {contactsList.length} contact{contactsList.length > 1 ? 's' : ''} found — select contacts to reveal details
                </Typography>

                {contactsList.map((contact, idx) => {
                  const isRevealed = revealedContactIndices.has(idx);
                  const isSelected = selectedContactIndices.has(idx);

                  return (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{
                        p: 2, mb: 1.5,
                        borderColor: isSelected ? 'primary.main' : isRevealed ? 'success.light' : 'grey.200',
                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.04)' : isRevealed ? 'rgba(76, 175, 80, 0.02)' : 'background.paper',
                        transition: 'all 0.2s',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        {!isRevealed && (
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleSelect(idx)}
                            size="small"
                            sx={{ mt: -0.5 }}
                          />
                        )}
                        {isRevealed && (
                          <VerifiedIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.5, flexShrink: 0 }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Position — always visible (contacts without position are filtered out) */}
                          <Chip label={contact.contactPosition} size="small" color="primary" variant="outlined" sx={{ mb: 0.75 }} />

                          {/* Name */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <PersonAddIcon sx={{ fontSize: 16, color: 'action.active' }} />
                            {isRevealed ? (
                              <Typography variant="body2" fontWeight={600}>{contact.contactName || 'N/A'}</Typography>
                            ) : (
                              <Typography variant="body2" sx={{ filter: 'blur(5px)', userSelect: 'none', fontWeight: 600 }}>
                                {contact.contactName || 'Contact Name'}
                              </Typography>
                            )}
                          </Box>

                          {/* Email — only show if available */}
                          {contact.contactEmail && contact.contactEmail !== 'null' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 16, color: 'action.active' }} />
                              {isRevealed ? (
                                <Typography variant="body2">{contact.contactEmail}</Typography>
                              ) : (
                                <Typography variant="body2" sx={{ filter: 'blur(5px)', userSelect: 'none' }}>
                                  {contact.contactEmail}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* LinkedIn */}
                          {contact.linkedInUrl && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinkedInIcon sx={{ fontSize: 16, color: 'action.active' }} />
                              {isRevealed ? (
                                <Typography
                                  variant="body2"
                                  component="button"
                                  onClick={() => contact.linkedInUrl && openLinkedInPopup(contact.linkedInUrl)}
                                  sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5,
                                    color: 'primary.main',
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    p: 0, font: 'inherit', textAlign: 'left',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  View Profile <OpenInNewIcon fontSize="inherit" />
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ filter: 'blur(5px)', userSelect: 'none' }}>
                                  linkedin.com/in/••••••••
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Thumb up/down feedback — visible once revealed */}
                          {isRevealed && (() => {
                            const fb = contactFeedback.get(idx);
                            const isUp = fb?.vote === 'UP';
                            const isDown = fb?.vote === 'DOWN';
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                                  Was this contact useful?
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleThumbUp(idx)}
                                  sx={{ color: isUp ? 'success.main' : 'action.active' }}
                                  title={isUp ? 'Click to remove your vote' : 'Mark as good lead'}
                                >
                                  {isUp ? <ThumbUpIcon sx={{ fontSize: 18 }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleThumbDown(idx)}
                                  sx={{ color: isDown ? 'error.main' : 'action.active' }}
                                  title={isDown ? 'Edit your feedback' : 'Mark as poor fit'}
                                >
                                  {isDown ? <ThumbDownIcon sx={{ fontSize: 18 }} /> : <ThumbDownOutlinedIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                                {isDown && fb?.rating != null && (
                                  <Typography variant="caption" sx={{ color: 'error.main', ml: 0.25 }}>
                                    {fb.rating}/5
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()}

                          {/* Add to CRM button — only for revealed contacts */}
                          {isRevealed && !addedContactIndices.has(idx) && (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={addingContactIdx === idx ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
                              onClick={() => {
                                setAddingContactIdx(idx);
                                onAddToCRM(currentLead!, {
                                  contactName: contact.contactName,
                                  email: contact.contactEmail,
                                }).then(() => {
                                  setAddedContactIndices(prev => {
                                    const next = new Set(prev);
                                    next.add(idx);
                                    return next;
                                  });
                                }).finally(() => {
                                  setAddingContactIdx(null);
                                });
                              }}
                              disabled={addingContactIdx !== null}
                              sx={{ mt: 1, borderRadius: 2, textTransform: 'none' }}
                            >
                              {addingContactIdx === idx ? 'Adding...' : 'Add to CRM'}
                            </Button>
                          )}
                          {isRevealed && addedContactIndices.has(idx) && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Added to CRM"
                              color="success"
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}

                {/* Action Buttons */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  {!allRevealed && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<RevealIcon />}
                      onClick={handleRevealSelected}
                      disabled={!hasSelection}
                      sx={{ borderRadius: 2 }}
                    >
                      {hasSelection
                        ? `Reveal ${selectedContactIndices.size} Contact${selectedContactIndices.size > 1 ? 's' : ''}`
                        : 'Select contacts to reveal'}
                    </Button>
                  )}
                  {!hasSearchedMore && !alreadySearchedMore && !noMoreContactsFound && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={isFindingMore ? <CircularProgress size={16} /> : <FindMoreIcon />}
                      onClick={handleFindMoreContacts}
                      disabled={isFindingMore}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      {isFindingMore ? 'Searching...' : 'Find More Contacts'}
                    </Button>
                  )}
                  {(hasSearchedMore || alreadySearchedMore) && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Additional search completed"
                      color="info"
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {noMoreContactsFound && !hasSearchedMore && !alreadySearchedMore && (
                    <Chip
                      label="No additional contacts found"
                      color="default"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </>
            );
          })()}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* AI Recommendation */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon color="primary" />
              AI Recommendation (AI 建議)
            </Typography>
          </Box>

          {displayRecommendation ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'primary.50',
                borderColor: 'primary.200'
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {displayRecommendation}
              </Typography>
            </Paper>
          ) : (
            <Alert severity="info">
              Run "Enrich & Recommend" to get AI-powered sales advice for this lead.
            </Alert>
          )}
        </Box>

        {/* Contact Form (if needed) */}
        <Collapse in={showContactForm}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Enter contact details to add to CRM:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Contact Name"
                  value={contactForm.contactName}
                  onChange={(e) => setContactForm(prev => ({ ...prev, contactName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Email *"
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Phone"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={isRejecting ? <CircularProgress size={16} /> : <ThumbDownIcon />}
          onClick={handleReject}
          disabled={isRejecting || addingContactIdx !== null}
        >
          {isRejecting ? 'Rejecting...' : 'Not Interested'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={addingContactIdx !== null}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {downvoteDialogIdx !== null && (() => {
      const fb = contactFeedback.get(downvoteDialogIdx);
      const isEditing = fb?.vote === 'DOWN';
      // Read the contact info from the same source the modal uses
      const meta = (currentLead?.metadata as any) || {};
      const contacts: any[] = Array.isArray(meta.contacts) ? meta.contacts : [];
      const target = contacts[downvoteDialogIdx] || {};
      return (
        <ContactDownvoteDialog
          open
          onClose={() => setDownvoteDialogIdx(null)}
          initial={isEditing ? { rating: fb?.rating, reasonChips: fb?.reasonChips, reasonText: fb?.reasonText } : null}
          contactName={target.contactName}
          contactPosition={target.contactPosition}
          onSubmit={(payload) => submitFeedback(downvoteDialogIdx, { vote: 'DOWN', ...payload })}
          onRemove={isEditing ? () => removeFeedback(downvoteDialogIdx) : undefined}
        />
      );
    })()}
    </>
  );
};

export default LeadDetailModal;
